import { CSSProperties, RefObject, useLayoutEffect, useRef, useState } from 'react';

const MAX_COLLAPSED_ROWS = 3;

type MeasurePhase = 'measuring' | 'fitting' | 'ready';

type GridMeasurement = {
    itemCount: number;
    naturalHeight: number;
};


type UseCollapsibleGridReturn<T> = {
    wrapperRef: RefObject<HTMLDivElement>;
    gridRef: RefObject<HTMLDivElement>;
    visibleValues: T[];
    wrapperStyle: CSSProperties;
    needsCollapse: boolean;
};

function getDistinctRowTops(rects: DOMRect[], gridTop: number): number[] {
    return [...new Set(rects.map(r => Math.round(r.top - gridTop)))].sort((a, b) => a - b);
}

function getItemsAboveRow(rects: DOMRect[], gridTop: number, rowTop: number): DOMRect[] {
    return rects.filter(r => Math.round(r.top - gridTop) <= rowTop);
}

function getBottomEdge(rects: DOMRect[], gridTop: number): number {
    return Math.ceil(Math.max(...rects.map(r => r.bottom - gridTop)));
}

function measureCollapsedRows(grid: HTMLDivElement): GridMeasurement | null {
    const gridTop = grid.getBoundingClientRect().top;
    const rects = Array.from(grid.children).map(c => c.getBoundingClientRect());
    const rowTops = getDistinctRowTops(rects, gridTop);

    if (rowTops.length <= MAX_COLLAPSED_ROWS) return null;

    const itemsInView = getItemsAboveRow(rects, gridTop, rowTops[MAX_COLLAPSED_ROWS - 1]);

    return {
        itemCount: itemsInView.length,
        naturalHeight: getBottomEdge(itemsInView, gridTop),
    };
}

function buttonFitsInRows(grid: HTMLDivElement, naturalHeight: number): boolean {
    const gridTop = grid.getBoundingClientRect().top;
    const button = grid.lastElementChild as HTMLElement;
    // Compare button's top (not bottom) to naturalHeight — robust against sub-pixel fractional widths
    // that can push the button bottom just past the boundary while it's visually still on row 3.
    return (button.getBoundingClientRect().top - gridTop) < naturalHeight;
}

function measureButtonBottom(grid: HTMLDivElement): number {
    const gridTop = grid.getBoundingClientRect().top;
    return Math.ceil((grid.lastElementChild as HTMLElement).getBoundingClientRect().bottom - gridTop);
}

export function useCollapsibleGrid<T extends { valueId?: number }>(
    items: T[],
    expanded: boolean
): UseCollapsibleGridReturn<T> {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);
    const [collapsedCount, setCollapsedCount] = useState<number | null>(null);
    const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
    const [wrapperHeight, setWrapperHeight] = useState<number | undefined>(undefined);
    const [measurePhase, setMeasurePhase] = useState<MeasurePhase>('measuring');
    const naturalCollapsedHeightRef = useRef<number | null>(null);

    const needsCollapse = collapsedCount !== null;

    // Phase 1 — all items are rendered; measure how many fit within MAX_COLLAPSED_ROWS.
    // Requires a real DOM pass, so it runs as a layout effect after the first paint.
    useLayoutEffect(() => {
        if (measurePhase !== 'measuring') return;
        const grid = gridRef.current;
        if (!grid) return;

        const measurement = measureCollapsedRows(grid);
        if (!measurement) return;

        naturalCollapsedHeightRef.current = measurement.naturalHeight;
        setCollapsedCount(measurement.itemCount);
        setMeasurePhase('fitting');
    }, [items.length, measurePhase]);

    // Phase 2 — only the collapsed items and the "View More" button are rendered.
    // Checks whether the button fits within the natural 3-row height using the button's
    // top position (robust against sub-pixel fractional widths that can push the bottom
    // just past the boundary). If the button overflows, one item is dropped and the effect
    // re-runs automatically (collapsedCount changed) until the button fits.
    useLayoutEffect(() => {
        if (measurePhase !== 'fitting') return;
        const grid = gridRef.current;
        if (!grid || collapsedCount === null || naturalCollapsedHeightRef.current === null) return;
        if (!grid.lastElementChild) return;

        const naturalHeight = naturalCollapsedHeightRef.current;
        if (buttonFitsInRows(grid, naturalHeight)) {
            setCollapsedHeight(measureButtonBottom(grid));
            setWrapperHeight(measureButtonBottom(grid));
            setMeasurePhase('ready');
        } else {
            setCollapsedCount(collapsedCount - 1);
        }
    }, [measurePhase, collapsedCount]);

    // Phase 3 — measurement is done; respond to expand/collapse toggling.
    // requestAnimationFrame defers the full-height write by one frame so the browser
    // has already committed the collapsed height, giving CSS transition something to animate from.
    useLayoutEffect(() => {
        if (measurePhase !== 'ready' || !wrapperRef.current) return;
        if (expanded) {
            const fullHeight = wrapperRef.current.scrollHeight;
            const raf = requestAnimationFrame(() => setWrapperHeight(fullHeight));
            return () => cancelAnimationFrame(raf);
        } else {
            setWrapperHeight(collapsedHeight);
        }
    }, [expanded, measurePhase, collapsedHeight]);

    const visibleValues = needsCollapse && !expanded
        ? items.slice(0, collapsedCount)
        : items;

    const wrapperStyle: CSSProperties = {
        overflow: 'hidden',
        height: wrapperHeight !== undefined ? `${wrapperHeight}px` : undefined,
        transition: measurePhase === 'ready' ? 'height 0.25s ease' : undefined,
    };

    return { wrapperRef: wrapperRef, gridRef: gridRef, visibleValues: visibleValues, wrapperStyle: wrapperStyle, needsCollapse: needsCollapse };
}
